import { useStoreTeamsQuery } from "@coworker/app/src/core/hooks/useStoreTeams";
import { useAllStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useAllStoreUsers";
import { User } from "@coworker/apprestructured/src/shared/types/user";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import { useTranslation } from "@coworker/locales";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { useMemo } from "react";

type GetAssignedUserArgs = {
  task: Task | Loading;
  users: User[] | undefined;
};
const getAssignedUser = ({ task, users }: GetAssignedUserArgs) => {
  if (isLoading(task)) {
    return LOADING;
  }

  if (task.assigned_user_id === null) {
    return null;
  }

  return (
    users?.find(({ uid }) => uid === task.assigned_user_id)?.fullName ?? LOADING
  );
};

type GetAssignedTeamArgs = {
  task: Task | Loading;
  teams: StoreTeam[] | undefined;
};
const getAssignedTeam = ({ task, teams = [] }: GetAssignedTeamArgs) => {
  if (isLoading(task)) {
    return LOADING;
  }
  return teams?.find(({ id }) => id === task.assigned_team_id)?.name;
};

type GetTaskCardAssigneeLabelArgs = {
  assignedUser: string | typeof LOADING | null;
  assignedTeam: string | typeof LOADING;
};
const getTaskCardAssigneeLabel = ({
  assignedUser,
  assignedTeam,
}: GetTaskCardAssigneeLabelArgs) => {
  if (assignedUser === null) {
    if (isLoading(assignedTeam)) {
      return LOADING;
    }
    return assignedTeam;
  }

  if (isLoading(assignedUser)) {
    return LOADING;
  }
  return assignedUser;
};

const useTaskCardAssigneeLabel = (task: Task | Loading) => {
  const { t } = useTranslation();

  const { data: teams, isLoading: isTeamsLoading } = useStoreTeamsQuery();
  const { data: users, isLoading: isUsersLoading } = useAllStoreUsers();
  const isLoading = isTeamsLoading || isUsersLoading;

  const label = useMemo(() => {
    if (isLoading) {
      return LOADING;
    }

    const assignedUser = getAssignedUser({ task, users });
    const assignedTeam = getAssignedTeam({ task, teams });
    const team = assignedTeam ?? t("unknownTeamString");
    const label = getTaskCardAssigneeLabel({
      assignedUser,
      assignedTeam: team,
    });
    return label;
  }, [task, teams, users, t, isLoading]);

  return label;
};

export default useTaskCardAssigneeLabel;
